// W A S D キーおよびカーソルキーでキャンバスを pan する。
// 押下中フラグを保持し、requestAnimationFrame ループで pan 量を加算することで滑らかに動く。
// SHIFT を併せて押すと 3 倍速で移動する。
// IME やフォーム入力コントロールにフォーカスがあるときは無効化する。
// 押下中の判定は「物理キー」単位で保持し、tick で論理方向（up/down/left/right）へ合成する。
// こうすることで、W と ↑ のような同方向の物理キーを同時押ししてから片方だけ離しても、
// もう一方が押されている間はパンが継続する（同方向キーの参照カウントを誤って 0 にしない）。

import { useEffect } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { isEditingTarget } from "./editing-target";

const PAN_SPEED_PX_PER_FRAME = 12;
const SHIFT_SPEED_MULTIPLIER = 3;

// 物理キー（KeyboardEvent.key を lower-case した値）から論理パン方向への対応表。
// WASD とカーソルキーの両方を同じ「論理方向」に解決して、後段の処理を共通化する。
const PAN_KEY_TO_DIRECTION: Record<string, "up" | "down" | "left" | "right" | undefined> = {
  w: "up",
  s: "down",
  a: "left",
  d: "right",
  arrowup: "up",
  arrowdown: "down",
  arrowleft: "left",
  arrowright: "right",
};

export function useKeyboardPan(): void {
  const setView = useCanvasStore((s) => s.setView);

  useEffect(() => {
    // 押下中の「物理キー」集合。論理方向への変換は tick 内で行う。
    // 同方向の異なる物理キー（例: W と ↑）を同時押ししても、片方の keyup で
    // 他方の判定を巻き添えに消さないようにするため、物理キー単位で保持する。
    const pressedKeys = new Set<string>();
    let shiftDown = false;
    let rafId: number | null = null;

    // 押下中の物理キーから、有効な論理方向の集合を毎フレーム合成する。
    const composeActiveDirections = (): Set<"up" | "down" | "left" | "right"> => {
      const directions = new Set<"up" | "down" | "left" | "right">();
      for (const key of pressedKeys) {
        const direction = PAN_KEY_TO_DIRECTION[key];
        if (direction) directions.add(direction);
      }
      return directions;
    };

    const tick = (): void => {
      const { view } = useCanvasStore.getState();
      const speed = PAN_SPEED_PX_PER_FRAME * (shiftDown ? SHIFT_SPEED_MULTIPLIER : 1);
      const directions = composeActiveDirections();
      let dx = 0;
      let dy = 0;
      if (directions.has("up")) dy += speed;
      if (directions.has("down")) dy -= speed;
      if (directions.has("left")) dx += speed;
      if (directions.has("right")) dx -= speed;
      if (dx !== 0 || dy !== 0) {
        setView({ ...view, panX: view.panX + dx, panY: view.panY + dy });
      }
      rafId = window.requestAnimationFrame(tick);
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.shiftKey) shiftDown = true;
      // Shift 単独はパン対象キーではない。Ctrl/Meta/Alt と組み合わせは pan を無効化する。
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditingTarget(event.target)) return;
      const key = event.key.toLowerCase();
      if (PAN_KEY_TO_DIRECTION[key]) {
        pressedKeys.add(key);
        if (rafId === null) {
          rafId = window.requestAnimationFrame(tick);
        }
        // カーソルキーはブラウザ既定のスクロール動作と衝突するため、必ず preventDefault する。
        event.preventDefault();
      }
    };

    const onKeyUp = (event: KeyboardEvent): void => {
      if (!event.shiftKey) shiftDown = false;
      const key = event.key.toLowerCase();
      if (PAN_KEY_TO_DIRECTION[key]) {
        pressedKeys.delete(key);
      }
      if (pressedKeys.size === 0 && rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    // ウィンドウのフォーカスが外れた直後に Shift キーが上がっても拾えるよう、blur で状態をリセット。
    const onBlur = (): void => {
      shiftDown = false;
      pressedKeys.clear();
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [setView]);
}
